import { ChangeEvent, useEffect, useMemo, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { updateCustomerGroupExportSettings } from "../../../services";
import {
  CUSTOMER_GROUP_EXPORT_FORMAT_LABEL,
  CUSTOMER_GROUP_EXPORT_FORMATS,
  CustomerGroupWithExportSettings,
} from "../../../types/customer-group-export.type";
import { PURCHASE_ORDER_CHAIN_LABEL } from "../../../types/purchase-order.type";
import { PURCHASE_ORDER_CHAIN_ORDER } from "../../PurchaseOrder/data";
import {
  MtExportDraft,
  MtExportDraftField,
  parseExportFormat,
  parsePoChain,
  parsePrefixes,
  toExportSettingsRequest,
  toMtExportDraft,
  usesLineItemLayout,
  validateMtExportDraft,
} from "./mtExport.utils";

interface MtExportSettingsDialogProps {
  /** The group being edited; null keeps the dialog closed. */
  group: CustomerGroupWithExportSettings | null;
  /** Every group, for the PO customer-code prefix choices. */
  groups: CustomerGroupWithExportSettings[];
  onClose: () => void;
  onSaved: () => void;
}

const NONE = "";

const EMPTY_DRAFT: MtExportDraft = {
  exportFormat: NONE,
  filePrefix: "",
  siteLength: "",
  docType: "",
  vatBranchNo: "",
  poChain: NONE,
  poPrefixes: [],
};

const renderPrefixes = (selected: unknown) => parsePrefixes(selected).join(", ");

/** One customer group's MT chain export + PO settings (MASTER). */
export default function MtExportSettingsDialog({
  group,
  groups,
  onClose,
  onSaved,
}: Readonly<MtExportSettingsDialogProps>) {
  const [draft, setDraft] = useState<MtExportDraft>(EMPTY_DRAFT);
  const [saving, setSaving] = useState<boolean>(false);

  // Every open starts from what is stored; a half-edited draft from last time is not a setting.
  useEffect(() => {
    if (group) setDraft(toMtExportDraft(group));
  }, [group]);

  const errors = useMemo(
    () => validateMtExportDraft(draft, group?.vendor_code ?? null),
    [draft, group],
  );
  const hasErrors = Object.keys(errors).length > 0;
  const hasLayout = draft.exportFormat !== NONE;
  const isLineItem = usesLineItemLayout(draft.exportFormat);
  const exampleFilename = `${draft.filePrefix || "…"}_DD-MM-YY.xlsx`;

  const handleFormatChange = (event: ChangeEvent<HTMLInputElement>) => {
    const exportFormat = parseExportFormat(event.target.value);
    setDraft((prev) => ({ ...prev, exportFormat }));
  };

  const handlePoChainChange = (event: ChangeEvent<HTMLInputElement>) => {
    const poChain = parsePoChain(event.target.value);
    setDraft((prev) => ({ ...prev, poChain }));
  };

  // A multiple select reports its value as an array, whatever the input's typing says.
  const handlePoPrefixesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const poPrefixes = parsePrefixes(event.target.value);
    setDraft((prev) => ({ ...prev, poPrefixes }));
  };

  const handleTextChange =
    (field: Exclude<MtExportDraftField, "poChain" | "poPrefixes">) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setDraft((prev) => ({ ...prev, [field]: value }));
    };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSave = async () => {
    if (!group || hasErrors) return;
    setSaving(true);
    try {
      await updateCustomerGroupExportSettings(group.uuid, toExportSettingsRequest(draft));
      onSaved();
    } catch {
      // Surfaced by the axios interceptor; keep the draft so MASTER can correct and retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={group !== null} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        ตั้งค่า export เชน — {group?.code} {group?.name}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color={errors.vendor ? "error" : "text.secondary"}>
            รหัส vendor: {group?.vendor_code ?? "ไม่มี"}
            {errors.vendor && ` — ${errors.vendor}`}
          </Typography>

          <TextField
            select
            size="small"
            label="รูปแบบไฟล์ export"
            value={draft.exportFormat}
            onChange={handleFormatChange}
            helperText="เชนใหม่ที่ใช้รูปแบบไฟล์เดิมตั้งค่าได้เลย · รูปแบบไฟล์ใหม่ต้องพัฒนาเพิ่ม"
          >
            <MenuItem value={NONE}>ไม่ export</MenuItem>
            {CUSTOMER_GROUP_EXPORT_FORMATS.map((format) => (
              <MenuItem key={format} value={format}>
                {CUSTOMER_GROUP_EXPORT_FORMAT_LABEL[format]}
              </MenuItem>
            ))}
          </TextField>

          {hasLayout && (
            <TextField
              size="small"
              label="ชื่อไฟล์ (prefix)"
              value={draft.filePrefix}
              onChange={handleTextChange("filePrefix")}
              error={Boolean(errors.filePrefix)}
              helperText={errors.filePrefix ?? `ไฟล์จะชื่อ ${exampleFilename}`}
            />
          )}

          {isLineItem && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                size="small"
                type="number"
                label="จำนวนหลักรหัสร้าน (site)"
                value={draft.siteLength}
                onChange={handleTextChange("siteLength")}
                error={Boolean(errors.siteLength)}
                helperText={errors.siteLength ?? "นับจากท้ายรหัสลูกค้า"}
                fullWidth
              />
              <TextField
                size="small"
                label="ประเภทเอกสาร (Doc type)"
                value={draft.docType}
                onChange={handleTextChange("docType")}
                error={Boolean(errors.docType)}
                helperText={errors.docType}
                fullWidth
              />
              <TextField
                size="small"
                label="เลขที่สาขา VAT"
                value={draft.vatBranchNo}
                onChange={handleTextChange("vatBranchNo")}
                error={Boolean(errors.vatBranchNo)}
                helperText={errors.vatBranchNo}
                fullWidth
              />
            </Stack>
          )}

          <Divider />
          <Typography variant="subtitle2">PO โมเดิร์นเทรด</Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField
              select
              size="small"
              label="เชน PO"
              value={draft.poChain}
              onChange={handlePoChainChange}
              error={Boolean(errors.poChain)}
              helperText={errors.poChain}
              fullWidth
            >
              <MenuItem value={NONE}>ไม่ผูกเชน PO</MenuItem>
              {PURCHASE_ORDER_CHAIN_ORDER.map((chain) => (
                <MenuItem key={chain} value={chain}>
                  {PURCHASE_ORDER_CHAIN_LABEL[chain]} ({chain})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="prefix รหัสลูกค้า (หลักที่ 5)"
              value={draft.poPrefixes}
              onChange={handlePoPrefixesChange}
              slotProps={{ select: { multiple: true, renderValue: renderPrefixes } }}
              error={Boolean(errors.poPrefixes)}
              helperText={errors.poPrefixes ?? "ร้านที่ PO ของเชนนี้จับคู่ได้"}
              fullWidth
            >
              {groups.map((option) => (
                <MenuItem key={option.uuid} value={option.prefix}>
                  {option.prefix} — {option.name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          ยกเลิก
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={hasErrors || saving}>
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
